import { pushApi } from './api';

/**
 * Web Push do painel: a notificação que chega com o app FECHADO.
 *
 * A inscrição é do APARELHO (o navegador gera um endpoint) e é gravada no
 * backend com o usuário logado. Este módulo faz a costura: pede a chave pública
 * ao backend, a permissão ao navegador, inscreve no service worker (o sw.js já
 * registrado em index.js) e manda a inscrição à API.
 *
 * `crm_push_device` no localStorage marca "este aparelho tem push": o App.js
 * usa para não mostrar a notificação própria (a da aba aberta) em cima da do
 * push, e para reatar a inscrição ao usuário no próximo login — num computador
 * compartilhado, o push segue quem entrou por último.
 */
export const PUSH_DEVICE_FLAG = 'crm_push_device';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isIos() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** Aberto como app instalado (tela de início), e não numa aba do navegador. */
export function isStandalone() {
  return Boolean(window.matchMedia?.('(display-mode: standalone)').matches)
    || window.navigator.standalone === true;
}

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';
}

/**
 * O registro do service worker. `ready` só resolve com um worker ativo — em
 * desenvolvimento ele não é registrado (index.js), então o timeout devolve
 * null em vez de pendurar a tela para sempre.
 */
async function registration() {
  if (!pushSupported()) return null;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((resolve) => setTimeout(() => resolve(null), 4000)),
  ]);
}

async function currentSubscription() {
  const reg = await registration();
  return reg ? reg.pushManager.getSubscription() : null;
}

function hasDeviceFlag() {
  try { return localStorage.getItem(PUSH_DEVICE_FLAG) === '1'; } catch { return false; }
}
function setDeviceFlag(on) {
  try {
    if (on) localStorage.setItem(PUSH_DEVICE_FLAG, '1');
    else localStorage.removeItem(PUSH_DEVICE_FLAG);
  } catch { /* storage bloqueado: só perde a conveniência */ }
}

/**
 * Situação deste aparelho, para a tela de perfil:
 *   unsupported  navegador sem push (ou painel em HTTP)
 *   ios-install  iPhone/iPad fora da tela de início — o iOS só entrega push a app instalado
 *   disabled     a plataforma está sem chaves VAPID
 *   denied       a pessoa bloqueou notificações no navegador
 *   on | off     inscrito neste aparelho, ou não
 */
export async function pushStatus() {
  if (!pushSupported()) return isIos() && !isStandalone() ? 'ios-install' : 'unsupported';
  if (isIos() && !isStandalone()) return 'ios-install';
  try {
    const cfg = await pushApi.config();
    if (!cfg.data?.enabled) return 'disabled';
  } catch {
    return 'disabled';
  }
  if (Notification.permission === 'denied') return 'denied';
  const sub = await currentSubscription();
  return sub && hasDeviceFlag() ? 'on' : 'off';
}

/** Ativa o push neste aparelho. Devolve { ok } ou { ok: false, reason }. */
export async function subscribeToPush() {
  if (!pushSupported()) return { ok: false, reason: isIos() && !isStandalone() ? 'ios-install' : 'unsupported' };
  if (isIos() && !isStandalone()) return { ok: false, reason: 'ios-install' };

  const cfg = await pushApi.config();
  if (!cfg.data?.enabled) return { ok: false, reason: 'disabled' };

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: perm };

  const reg = await registration();
  if (!reg) return { ok: false, reason: 'no-sw' };

  const key = urlBase64ToUint8Array(cfg.data.public_key);
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // Inscrição feita com outra chave (a plataforma trocou o par VAPID): o
    // navegador recusa reinscrever por cima — descarta e inscreve de novo.
    const existingKey = sub.options?.applicationServerKey;
    const same = existingKey && existingKey.byteLength === key.byteLength
      && new Uint8Array(existingKey).every((b, i) => b === key[i]);
    if (!same) { await sub.unsubscribe().catch(() => {}); sub = null; }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  }

  await pushApi.subscribe(sub.toJSON());
  setDeviceFlag(true);
  return { ok: true };
}

/** Desliga o push neste aparelho: apaga no backend e no navegador. */
export async function unsubscribeFromPush() {
  const sub = await currentSubscription();
  if (sub) {
    await pushApi.unsubscribe(sub.endpoint).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  setDeviceFlag(false);
  return { ok: true };
}

/**
 * No login: se este aparelho já tem push, reata a inscrição ao usuário que
 * acabou de entrar. Silencioso — não pede permissão nem mostra nada.
 */
export async function resyncPush() {
  if (!hasDeviceFlag() || !pushSupported() || Notification.permission !== 'granted') return;
  try {
    const sub = await currentSubscription();
    if (sub) await pushApi.subscribe(sub.toJSON());
  } catch { /* sem rede ou sem chaves: o perfil mostra a situação real */ }
}

/**
 * No logout: solta a inscrição do usuário no backend, mas mantém a do
 * navegador e a marca do aparelho — o próximo login reata sem pedir nada.
 * Sem isto, quem saiu continuaria recebendo push de conversas neste aparelho.
 */
export async function detachPush(token) {
  if (!hasDeviceFlag() || !pushSupported()) return;
  try {
    const sub = await currentSubscription();
    if (sub) await pushApi.unsubscribe(sub.endpoint, token);
  } catch { /* o backend apaga a inscrição sozinho quando o push falhar */ }
}
